import { Link } from "react-router-dom";
import { useLocale } from "@/i18n";

type GuestAuthPromptProps = {
  guestLoggedIn: boolean;
  /** Prominent banner at page top vs. inline hint in a form step */
  variant?: "banner" | "inline";
  /** Booking flow vs. post-submit confirmation */
  context?: "booking" | "confirmation";
  returnTo?: string;
  className?: string;
};

export function GuestAuthPrompt({
  guestLoggedIn,
  variant = "banner",
  context = "booking",
  returnTo = "/buchen",
  className = "",
}: GuestAuthPromptProps) {
  const { messages: m, localePath } = useLocale();

  if (guestLoggedIn) {
    return (
      <div className={`text-center ${className}`.trim()}>
        <Link
          to={localePath("/portal")}
          className="bk-btn-secondary inline-flex min-w-[12rem] items-center justify-center"
        >
          {m.guestAuth.portal}
        </Link>
      </div>
    );
  }

  const loginTo = { pathname: localePath("/login"), state: { returnTo } };

  if (variant === "inline") {
    const inlineTitle =
      context === "confirmation" ? m.guestAuth.manageStays : m.guestAuth.alreadyBooked;
    const inlineBody =
      context === "confirmation" ? m.guestAuth.manageStaysHint : m.login.subtitleBooking;

    return (
      <div
        className={`rounded-xl border border-brand-100 bg-brand-50/70 px-4 py-4 ${className}`.trim()}
        role="region"
        aria-label={m.guestAuth.inlineRegion}
      >
        <p className="font-semibold text-brand-900">{inlineTitle}</p>
        <p className="mt-1 text-sm text-slate-700">{inlineBody}</p>
        <Link
          to={loginTo}
          className="bk-btn-primary mt-3 inline-flex w-full items-center justify-center sm:w-auto"
        >
          {m.guestAuth.loginNow}
        </Link>
      </div>
    );
  }

  return (
    <section
      className={`flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between ${className}`.trim()}
      role="region"
      aria-label={m.guestAuth.returningGuestsRegion}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{m.guestAuth.alreadyBooked}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
          {m.guestAuth.alreadyBookedHint} {m.guestAuth.loginCodeHint}
        </p>
      </div>
      <Link
        to={loginTo}
        className="bk-btn-secondary shrink-0 px-4 py-2 text-xs sm:text-sm"
      >
        {m.guestAuth.login}
      </Link>
    </section>
  );
}
